import React, { Component } from 'react';
import {
    Row, Col,
    Modal, ModalHeader, ModalBody, ModalFooter,
    Form, FormGroup, Label, Input,
    Alert, Card
} from 'reactstrap';
import Button from 'reactstrap/lib/Button';
import Select, { components } from 'react-select';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faTrash, faPlus } from '@fortawesome/pro-solid-svg-icons';

// Custom dropdown indicator for react-select
const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <span style={{
                backgroundColor: '#28a745',
                color: 'white',
                padding: '4px 6px',
                borderRadius: '4px',
                fontSize: '0.85rem',
                fontWeight: '500',
                cursor: 'pointer'
            }}>Add
            </span>
        </components.DropdownIndicator>
    );
};

class CreateOrderBatchModal extends Component {
    state = {
        currentStep: 1,
        selectedConfigurations: [],
        batchId: null,
        batchName: '',
        aggregatedProducts: [],
        separateProducts: false,
        loading: false,
        error: null,
        success: false,
        successMsg: '',
        copied: false,
        storeUrl: process.env.REACT_APP_STORE_URL || 'https://www.taydaelectronics.com'
    }

    componentDidUpdate(prevProps) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.setState({
                currentStep: 1,
                selectedConfigurations: [],
                batchId: null,
                batchName: '',
                aggregatedProducts: [],
                separateProducts: false,
                loading: false,
                error: null,
                success: false,
                successMsg: '',
                copied: false
            });
        }
    }

    getConfigurationOptions = () => {
        const { configurations } = this.props;
        if (!configurations) return [];

        return configurations.map(config => ({
            value: config.id,
            label: `${config.name} - ${config.enclosure_type || ''} ${config.powder_color || ''}`.trim(),
            config: config
        }));
    }

    handleConfigurationSelect = (selectedOptions) => {
        if (!selectedOptions) {
            this.setState({ selectedConfigurations: [] });
            return;
        }

        if (selectedOptions.length > 10) {
            this.setState({ error: 'You can select up to 10 configurations maximum.' });
            return;
        }

        const newSelections = selectedOptions.map(option => {
            const existing = this.state.selectedConfigurations.find(s => s.id === option.value);
            return {
                id: option.value,
                name: option.config.name,
                config: option.config,
                qty: existing ? existing.qty : 1
            };
        });

        this.setState({ selectedConfigurations: newSelections, error: null });
    }

    handleQtyChange = (configId, newQty) => {
        const qty = parseInt(newQty) || 1;
        if (qty < 1) return;

        this.setState(prevState => ({
            selectedConfigurations: prevState.selectedConfigurations.map(config =>
                config.id === configId ? { ...config, qty } : config
            )
        }));
    }

    // Calculate placeholder quantities based on selected configurations
    calculatePlaceholderQuantities = (aggregatedProducts, selectedConfigurations) => {
        // Count total drill and UV items across all configurations × their quantities
        let totalDrillQty = 0;
        let totalUvQty = 0;

        selectedConfigurations.forEach(config => {
            const configItems = config.config.box_configuration_items || [];
            const drillItems = configItems.filter(item => item.product_type === 'drill');
            const uvItems = configItems.filter(item => item.product_type === 'uv');

            totalDrillQty += drillItems.length * config.qty;
            totalUvQty += uvItems.length * config.qty;
        });

        // Update placeholder quantities in the aggregated products
        return aggregatedProducts.map(product => {
            if (product.is_placeholder) {
                // Determine if it's a drill or UV placeholder based on service_type or SKU
                const isDrillPlaceholder = product.service_type === 'drill' ||
                    (product.sku && product.sku.toUpperCase().includes('DR'));
                const isUvPlaceholder = product.service_type === 'uv' ||
                    (product.sku && product.sku.toUpperCase().includes('UV'));

                if (isDrillPlaceholder && totalDrillQty > 0) {
                    return { ...product, qty: totalDrillQty, required_qty: totalDrillQty };
                } else if (isUvPlaceholder && totalUvQty > 0) {
                    return { ...product, qty: totalUvQty, required_qty: totalUvQty };
                }
            }
            return product;
        });
    }

    removeConfiguration = (configId) => {
        this.setState(prevState => ({
            selectedConfigurations: prevState.selectedConfigurations.filter(c => c.id !== configId)
        }));
    }

    createBatchAndProceed = async () => {
        const { selectedConfigurations } = this.state;
        const { os } = this.props;

        if (selectedConfigurations.length === 0) {
            this.setState({ error: 'Please select at least one configuration.' });
            return;
        }

        this.setState({ loading: true, error: null });

        const batchName = `${moment().format('MMM DD, YYYY h:mm A')}`;
        const batchItems = selectedConfigurations.map((config, index) => ({
            configuration_id: config.id,
            qty: config.qty,
            position: index + 1
        }));

        try {
            const token = JSON.parse(localStorage.getItem("token"));
            const result = await os.createBoxConfigurationBatch(token, batchName, batchItems);

            if (result.error) {
                this.setState({ loading: false, error: result.error });
                return;
            }

            // API returns data wrapped in box_configuration_batch
            const batch = result.box_configuration_batch || result;

            // Recalculate placeholder quantities based on selected configurations
            const rawProducts = batch.aggregated_product_requirements || [];
            const correctedProducts = this.calculatePlaceholderQuantities(rawProducts, selectedConfigurations);

            this.setState({
                loading: false,
                batchId: batch.id,
                batchName: batchName,
                aggregatedProducts: correctedProducts,
                currentStep: 2
            });
        } catch (err) {
            this.setState({
                loading: false,
                error: err.message || 'Failed to create batch. Please try again.'
            });
        }
    }

    generateAddToCartUrl = (products, includePlaceholder = false) => {
        if (!products || products.length === 0) return null;

        let filteredProducts = includePlaceholder
            ? products
            : products.filter(product => !product.is_placeholder);

        if (filteredProducts.length === 0) return null;

        const skus = filteredProducts.map(product => product.sku).join(',');
        const qtys = filteredProducts.map(product => product.qty || product.required_qty || 1).join(',');

        return `${this.state.storeUrl}/direct/index/add/?product=${skus}&qty=${qtys}`;
    }

    copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            this.setState({ copied: true });
            if (this._copyTimer) clearTimeout(this._copyTimer);
            this._copyTimer = setTimeout(() => this.setState({ copied: false }), 1500);
        }).catch((err) => {
            console.error('Failed to copy: ', err);
        });
    }

    getProductsDiv = (products, includePlaceholder = false) => {
        if (!includePlaceholder) {
            products = products.filter(product => !product.is_placeholder);
        }
        const allProductsText = products.map(product =>
            `${product.sku}, ${product.qty || product.required_qty || 1}`
        ).join('\n');

        return (
            <Card className="bg-light p-2">
                {products.length > 0 && (
                    <div>
                        {this.state.copied ? (
                            <FontAwesomeIcon icon={faCheck} className='float-right text-success' alt='Copied' title='Copied'
                                style={{ marginLeft: '10px' }}
                            />
                        ) : (
                            <FontAwesomeIcon icon={faCopy} className='float-right' alt='Copy to Clipboard' title='Copy to Clipboard'
                                style={{ marginLeft: '10px', cursor: 'pointer' }}
                                onClick={() => this.copyToClipboard(allProductsText)}
                            />
                        )}
                        {products.map((product, index) => (
                            <div key={`batchProduct-${product.sku}-${index}`}>
                                <div title={product.product_name || product.name}>
                                    {product.sku},{product.qty || product.required_qty || 1}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        );
    }

    showProductCopyDiv = () => {
        const { aggregatedProducts, separateProducts } = this.state;

        if (separateProducts) {
            const enclProducts = aggregatedProducts.filter(p => p.service_type === null || p.is_placeholder);
            const serviceProducts = aggregatedProducts.filter(p => p.service_type !== null && !p.is_placeholder);
            const enclosureCartUrl = this.generateAddToCartUrl(enclProducts, true);
            const serviceCartUrl = this.generateAddToCartUrl(serviceProducts, false);

            return (
                <>
                    <Row>
                        <Col className="col" md={6}>
                            <h5>Enclosure Products</h5>
                            {this.getProductsDiv(enclProducts, true)}
                            {enclosureCartUrl && (
                                <a href={enclosureCartUrl} target="_blank" rel="noopener noreferrer" className="btn btn-success btn-sm btn-block mt-2">
                                    Add Enclosures to Cart
                                </a>
                            )}
                        </Col>
                        <Col className="col" md={6}>
                            <h5>Service Products</h5>
                            {this.getProductsDiv(serviceProducts)}
                            {serviceCartUrl && (
                                <a href={serviceCartUrl} target="_blank" rel="noopener noreferrer" className="btn btn-success btn-sm btn-block mt-2">
                                    Add Service Products to Cart
                                </a>
                            )}
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col className="col" md={6}>
                            <div className="text-danger">
                                <p className="my-1">When placing separate orders, include the exact quantity of Drill and UV PLACEHOLDER products that will be used in the linked Service order.</p>
                                <h6 className="my-1">Without PLACEHOLDER products, we can't link the orders correctly.</h6>
                            </div>
                        </Col>
                        <Col className="col" md={6}>
                            <div className="text-danger">
                                <p className="my-1">To link a Service order to an Enclosure order, enter the Enclosure order number in the checkout comment, surrounded by % signs: '%1009999999%'.</p>
                            </div>
                        </Col>
                    </Row>
                </>
            );
        } else {
            return (
                <>
                    <Row>
                        <Col className="col" md={6}>
                            <h5>All Products</h5>
                            {this.getProductsDiv(aggregatedProducts)}
                        </Col>
                    </Row>
                </>
            );
        }
    }

    reserveAndAddToCart = async () => {
        const { batchId, aggregatedProducts, batchName } = this.state;
        const { os } = this.props;

        this.setState({ loading: true, error: null });

        try {
            const token = JSON.parse(localStorage.getItem("token"));
            const result = await os.reserveBoxConfigurationBatch(token, batchId, 1);

            if (result.error) {
                this.setState({ loading: false, error: result.error });
                return;
            }

            const cartUrl = this.generateAddToCartUrl(aggregatedProducts, false);
            if (cartUrl) {
                window.open(cartUrl, '_blank');
            }

            // Store success message in localStorage for Dashboard to display
            localStorage.setItem('batchReservationSuccess', JSON.stringify({
                success: true,
                batchName: batchName,
                message: `Batch "${batchName}" reserved successfully! Complete your purchase on TaydaElectronics.com, then return to this Dashboard to track your order status.`
            }));

            this.setState({
                loading: false,
                success: true,
                successMsg: 'Batch reserved! Redirecting to Dashboard...'
            });

            // Redirect to Dashboard after short delay
            setTimeout(() => {
                window.location.href = '/dashboard';
            }, 1000);

        } catch (err) {
            this.setState({
                loading: false,
                error: err.message || 'Failed to reserve batch. Please try again.'
            });
        }
    }

    renderStep1 = () => {
        const { selectedConfigurations, loading, error } = this.state;
        const options = this.getConfigurationOptions();
        const selectedValues = selectedConfigurations.map(c =>
            options.find(o => o.value === c.id)
        ).filter(Boolean);

        return (
            <div>
                <Alert color="info" className="mb-3">
                    <strong>Step 1:</strong> Select up to 10 Box Job Templates and set the quantity for each.
                </Alert>

                <FormGroup>
                    <Label><strong>Select Box Templates ({selectedConfigurations.length}/10)</strong></Label>
                    <Select
                        isMulti
                        options={options}
                        value={selectedValues}
                        onChange={this.handleConfigurationSelect}
                        placeholder="Search and Select Box Job Templates..."
                        className="mb-3"
                        isDisabled={loading}
                        closeMenuOnSelect={true}
                        blurInputOnSelect={true}
                        components={{ DropdownIndicator }}
                    />
                </FormGroup>

                {selectedConfigurations.length > 0 && (
                    <div className="mb-3">
                        <Label><strong>Set Quantities:</strong></Label>
                        {selectedConfigurations.map((config, index) => (
                            <Card key={`selected-${config.id}`} className="p-2 mb-2 d-flex flex-row align-items-center">
                                <div className="flex-grow-1">
                                    <strong>{config.name}</strong>
                                    {config.config.enclosure_type && (
                                        <span className="text-muted ml-2">
                                            ({config.config.enclosure_type} {config.config.powder_color || ''})
                                        </span>
                                    )}
                                </div>
                                <div className="d-flex align-items-center">
                                    <Label className="mb-0 mr-2">Qty:</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={config.qty}
                                        onChange={(e) => this.handleQtyChange(config.id, e.target.value)}
                                        style={{ width: '80px' }}
                                        disabled={loading}
                                    />
                                    <Button
                                        color="link"
                                        className="text-danger ml-2 p-0"
                                        onClick={() => this.removeConfiguration(config.id)}
                                        disabled={loading}
                                        title="Remove"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}

                {error && <Alert color="danger">{error}</Alert>}

                <div className="text-right mt-3">
                    <Button
                        color="primary"
                        onClick={this.createBatchAndProceed}
                        disabled={loading || selectedConfigurations.length === 0}
                    >
                        {loading ? 'Creating Box Jobs...' : 'Next Step →'}
                    </Button>
                </div>
            </div>
        );
    }

    renderStep2 = () => {
        const { aggregatedProducts, separateProducts, loading, error, success, successMsg, batchName, selectedConfigurations } = this.state;

        return (
            <div>
                <Alert color="info" className="mb-3">
                    <strong>Step 2:</strong> Review products and add to cart. Your box jobs will be reserved when you click the button below.
                </Alert>

                <Card className="p-3 mb-3 bg-light">
                    <h6 className="mb-2"><strong>Order: {batchName}</strong></h6>
                    <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                        {selectedConfigurations.map((config, idx) => (
                            <div key={`summary-${config.id}`}>
                                {config.name} x {config.qty}
                            </div>
                        ))}
                    </div>
                </Card>

                <h5 className="mb-3">Required Products ({aggregatedProducts.filter(p => !p.is_placeholder).length})</h5>

                {this.showProductCopyDiv()}

                {/* <FormGroup check className="mt-3">
                    <Label check>
                        <Input
                            type="checkbox"
                            checked={separateProducts}
                            onChange={() => this.setState({ separateProducts: !separateProducts })}
                        />{' '}
                        Separate Enclosures and Service Products
                    </Label>
                </FormGroup> */}

                {error && <Alert color="danger" className="mt-3">{error}</Alert>}

                {success && (
                    <Alert color="success" className="mt-3">
                        <h5 className="alert-heading">Success!</h5>
                        {successMsg}
                    </Alert>
                )}

                {!success && (
                    <>
                        <Alert color="warning" className="mt-4">
                            <strong>Ready to order?</strong> You are about to place an order for all parts and services and reserve all box job templates in this batch. We will automatically begin building your box jobs as soon as your order is received.
                            <div className="mt-1 text-danger">
                                Make sure your Drill and UV Templates are correct because once you place your order, you can't change the templates attached to the box jobs in this batch. If you need to make changes after placing your order, please contact us immediately.
                            </div>
                        </Alert>
                        <div className="d-flex justify-content-between mt-3">
                            <Button
                                color="secondary"
                                outline
                                onClick={() => this.setState({ currentStep: 1 })}
                                disabled={loading}
                            >
                                ← Back
                            </Button>
                            <Button
                                color="success"
                                onClick={this.reserveAndAddToCart}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : 'Reserve Box Jobs & Create Order'}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    render() {
        const { isOpen, toggle } = this.props;
        const { currentStep } = this.state;

        return (
            <Modal
                isOpen={isOpen}
                toggle={toggle}
                size="lg"
                style={{ top: '60px' }}
            >
                <ModalHeader toggle={toggle}>
                    Create Order - Select Box Job Templates
                </ModalHeader>
                <ModalBody>
                    {currentStep === 1 && this.renderStep1()}
                    {currentStep === 2 && this.renderStep2()}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>Close</Button>
                </ModalFooter>
            </Modal>
        );
    }
}

export default CreateOrderBatchModal;
